                        
import { trimmedStringFromUnknown } from "./shared.js";
import * as z$1 from "zod/v4";
//#region src/schemas/protocol/profile.ts
const PROFILE_KIND_VALUES = [
	"human",
	"agent",
	"system"
];
const PROFILE_HANDLE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const UUID_PROFILE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const AGENT_PROFILE_ID_PATTERN = /^agt_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const MAX_PROFILE_HANDLE_LENGTH = 128;
const normalizeOptionalProfileLabel = (value) => {
	const normalized = trimmedStringFromUnknown(value);
	if (normalized.length === 0) return;
	return normalized.slice(0, 64);
};
const normalizeOptionalProfileHandle = (value) => {
	const normalized = trimmedStringFromUnknown(value);
	if (normalized.length === 0) return;
	return profileHandleSchema.safeParse(normalized).success ? normalized : void 0;
};
const MAX_PROFILE_WORKING_DIRECTORY_LENGTH = 4096;
const profileWorkspaceModeSchema = z$1.enum(["openmeld_managed", "custom"]);
const profileWorkingDirectorySchema = z$1.string().min(1).max(MAX_PROFILE_WORKING_DIRECTORY_LENGTH).refine((value) => !value.includes("\0"), { message: "workingDirectory must not contain NUL bytes" });
const normalizeOptionalWorkingDirectory = (value) => {
	if (typeof value !== "string") return;
	const parsed = profileWorkingDirectorySchema.safeParse(value);
	return parsed.success ? parsed.data : void 0;
};
const MAX_AVATAR_URL_LENGTH = 2048;
const HTTPS_URL_PATTERN = /^https?:\/\//i;
/**
* Avatar URLs are a public identity field that travels with the Profile across
* domains. Only http(s) URLs are accepted so the value is safe to render as an
* `<img src>` on web clients (rejects javascript:/data: injection vectors).
*/
const normalizeOptionalAvatarUrl = (value) => {
	const normalized = trimmedStringFromUnknown(value);
	if (normalized.length === 0 || normalized.length > 2048) return;
	return HTTPS_URL_PATTERN.test(normalized) ? normalized : void 0;
};
/** Unknown or invalid kinds are normalized to "agent" for wire compatibility. */
const profileKindWithFallbackSchema = z$1.preprocess((value) => {
	const parsed = z$1.enum(PROFILE_KIND_VALUES).safeParse(value);
	return parsed.success ? parsed.data : "agent";
}, z$1.enum(PROFILE_KIND_VALUES));
const profileKindSchema = z$1.enum(PROFILE_KIND_VALUES);
const profileLabelSchema = z$1.string().min(1).max(64);
const profileHandleSchema = z$1.string().min(1).max(128).regex(PROFILE_HANDLE_PATTERN);
const genericProfileIdSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(64));
const uuidProfileIdSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(64).regex(UUID_PROFILE_ID_PATTERN));
const agentProfileIdSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(64).regex(AGENT_PROFILE_ID_PATTERN));
/**
* Runtime OpenMeld profile IDs are auth-backed and follow these rules:
* - human/system profiles use a UUID
* - agent profiles use agt_<UUID>
*
* Generic test fixtures or legacy intermediary strings should not reuse this
* schema unless they represent a real runtime profile identity.
*/
const openMeldRuntimeProfileIdSchema = z$1.preprocess(trimmedStringFromUnknown, z$1.string().min(1).max(64).refine((value) => UUID_PROFILE_ID_PATTERN.test(value) || AGENT_PROFILE_ID_PATTERN.test(value), "Invalid OpenMeld runtime profile ID"));
/**
* Generic wire/profile normalization rules:
* - trims and validates profileId as a non-empty string
* - defaults missing/invalid kind to "agent"
* - truncates profileName/owner labels to max length
* - preserves agent workingDirectory verbatim when it is a valid string
* - keeps owner only for agent profiles
*
* This schema intentionally stays broader than auth-backed runtime IDs because
* protocol/system messages may still use internal synthetic profile IDs such as
* `openmeld-system`.
*/
const profileSchema = z$1.object({
	kind: profileKindWithFallbackSchema.optional(),
	profileId: genericProfileIdSchema,
	profileHandle: z$1.unknown().optional(),
	profileName: z$1.unknown().optional(),
	owner: z$1.unknown().optional(),
	ownerName: z$1.unknown().optional(),
	agentControllerRef: z$1.unknown().optional(),
	agentControllerConversationId: z$1.unknown().optional(),
	workingDirectory: z$1.unknown().optional(),
	avatarUrl: z$1.unknown().optional()
}).transform((value) => {
	const kind = value.kind ?? "agent";
	const profileHandle = normalizeOptionalProfileHandle(value.profileHandle);
	const profileName = normalizeOptionalProfileLabel(value.profileName);
	const owner = kind === "agent" ? normalizeOptionalProfileLabel(value.owner) : void 0;
	const ownerName = kind === "agent" ? normalizeOptionalProfileLabel(value.ownerName) : void 0;
	const agentControllerRef = kind === "agent" ? normalizeOptionalProfileLabel(value.agentControllerRef) : void 0;
	const agentControllerConversationId = kind === "agent" ? normalizeOptionalProfileLabel(value.agentControllerConversationId) : void 0;
	const workingDirectory = kind === "agent" ? normalizeOptionalWorkingDirectory(value.workingDirectory) : void 0;
	const avatarUrl = normalizeOptionalAvatarUrl(value.avatarUrl);
	return {
		kind,
		profileId: value.profileId,
		...profileHandle ? { profileHandle } : {},
		...profileName ? { profileName } : {},
		...owner ? { owner } : {},
		...ownerName ? { ownerName } : {},
		...agentControllerRef ? { agentControllerRef } : {},
		...agentControllerConversationId ? { agentControllerConversationId } : {},
		...workingDirectory ? { workingDirectory } : {},
		...avatarUrl ? { avatarUrl } : {}
	};
});
//#endregion
export { MAX_AVATAR_URL_LENGTH, MAX_PROFILE_HANDLE_LENGTH, MAX_PROFILE_WORKING_DIRECTORY_LENGTH, agentProfileIdSchema, genericProfileIdSchema, normalizeOptionalWorkingDirectory, openMeldRuntimeProfileIdSchema, profileHandleSchema, profileKindSchema, profileLabelSchema, profileSchema, profileWorkingDirectorySchema, profileWorkspaceModeSchema, uuidProfileIdSchema };

//# sourceMappingURL=profile.js.map